import retry from 'async-retry';
import 'isomorphic-fetch';

// Use "globalThis" instead of "window", "self", or "global" (javascript:S7764)
const window = globalThis.window;

/**
 * Fetches a URL with retry logic for transient errors.
 *
 * @param {string} url - The URL to fetch.
 * @param {RequestInit} [init] - Optional fetch initialization parameters.
 * @param {object} [opts] - Optional settings for retries.
 * @param {number} [opts.retries=3] - The maximum number of retry attempts.
 * @param {number} [opts.factor=2] - The exponential backoff factor.
 * @param {number} [opts.minTimeout=100] - The minimum timeout between retries in milliseconds.
 * @return {Promise<Response>} A promise that resolves to the fetch response.
 * @throws {Error} If the request fails with a non-transient error or exceeds retry attempts.
 */
export const fetchRetry = async (url, init = undefined, opts = {}) => {
  // is url absolute or relative? If relative, prepend the base URL
  const isAbsoluteUrl = /^https?:\/\//i.test(url);
  const fullUrl = isAbsoluteUrl ? url : `${window.location.origin}${url}`;

  return retry(
    async bail => {
      const res = await fetch(fullUrl, init);

      if (!res.ok) {
        switch (res.status) {
          case 408: // Request Timeout	The server took too long; a second attempt might squeeze through.
          case 429: // Too Many Requests	You're being rate-limited. (Wait for the Retry-After header if possible).
          case 500: // Internal Server Error	Often a temporary glitch or a single dying container.
          case 502: // Bad Gateway	Usually a temporary routing or proxy issue during a deploy.
          case 503: // Service Unavailable	The server is overloaded or down for maintenance.
          case 504: // Gateway Timeout	The upstream server didn't respond in time.
            // These are transient errors that may succeed on retry.
            // Throwing an error will trigger a retry.
            throw new Error(`Transient error with status ${res.status}`);
          case 401: // Unauthorized	Authentication failed. Check API keys.
          case 403: // Forbidden	You don't have permissions to access this resource.
            bail(new Error(`Access denied with status ${res.status}`));

            return;
          default:
            // These are not transient errors. Bail out without retrying.
            bail(new Error(`Request failed with status ${res.status}`));

            return;
        }
      }

      return res;
    },
    {
      retries: Number(opts?.retries ?? 3),
      factor: Number(opts?.factor ?? 2),
      minTimeout: Number(opts?.minTimeout ?? 100),
    },
  );
};
